import { Link } from 'react-router-dom';
import { Home } from 'lucide-react';

const ShippingPolicy = () => {
  return (
    <div className="min-h-screen bg-[#F5F1EA]" data-testid="shipping-policy-page">
      {/* Header */}
      <div className="border-b border-[#0A0A0A]/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-[#525252] hover:text-[#0A0A0A] transition-colors w-fit">
            <Home className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-heading font-bold text-[#0A0A0A] mb-2">Shipping Policy</h1>
        <p className="text-[#525252] mb-8">Last updated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

        <div className="prose prose max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">1. Shipping Coverage</h2>
            <p className="text-[#525252] leading-relaxed">
              Sol Digital ships NFC cards across India. We currently deliver to all serviceable pin codes covered by our logistics partners. International shipping is not available at this time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">2. Shipping Charges</h2>
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
              <table className="w-full text-[#525252]">
                <thead>
                  <tr className="border-b border-[#0A0A0A]/10">
                    <th className="text-left py-3 text-[#0A0A0A]">Order Value</th>
                    <th className="text-left py-3 text-[#0A0A0A]">Shipping Charge</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#0A0A0A]/10">
                    <td className="py-3">All Orders</td>
                    <td className="py-3">Free Shipping</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[#525252] leading-relaxed mt-4">
              Shipping charges are included in the product price. No additional shipping fees will be charged at checkout.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">3. Processing Time</h2>
            <p className="text-[#525252] leading-relaxed mb-4">
              Orders are processed within 1-2 business days after payment confirmation. Processing includes:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Order verification</li>
              <li>NFC card programming</li>
              <li>Quality check</li>
              <li>Packaging</li>
            </ul>
            <p className="text-[#525252] leading-relaxed mt-4">
              <strong>Note:</strong> Orders placed on weekends or public holidays will be processed on the next business day.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">4. Delivery Timeframes</h2>
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
              <table className="w-full text-[#525252]">
                <thead>
                  <tr className="border-b border-[#0A0A0A]/10">
                    <th className="text-left py-3 text-[#0A0A0A]">Location</th>
                    <th className="text-left py-3 text-[#0A0A0A]">Estimated Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#0A0A0A]/10">
                    <td className="py-3">Metro Cities (Delhi, Mumbai, Bangalore, Chennai, Kolkata, Hyderabad)</td>
                    <td className="py-3">3-5 business days</td>
                  </tr>
                  <tr className="border-b border-[#0A0A0A]/10">
                    <td className="py-3">Tier 2 Cities</td>
                    <td className="py-3">5-7 business days</td>
                  </tr>
                  <tr className="border-b border-[#0A0A0A]/10">
                    <td className="py-3">Other Areas</td>
                    <td className="py-3">7-10 business days</td>
                  </tr>
                  <tr>
                    <td className="py-3">Remote/Rural Areas</td>
                    <td className="py-3">10-14 business days</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[#525252] leading-relaxed mt-4">
              Delivery times are estimates and may vary due to factors beyond our control such as weather conditions, natural disasters, or courier delays.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">5. Shipping Partners</h2>
            <p className="text-[#525252] leading-relaxed mb-4">
              We work with reputed logistics partners to ensure safe and timely delivery:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Delhivery</li>
              <li>Blue Dart</li>
              <li>DTDC</li>
              <li>India Post (for remote areas)</li>
            </ul>
            <p className="text-[#525252] leading-relaxed mt-4">
              The shipping partner may vary based on your delivery location and product availability.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">6. Order Tracking</h2>
            <p className="text-[#525252] leading-relaxed mb-4">
              Once your order is shipped, you will receive:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Shipment confirmation email with tracking number</li>
              <li>SMS notification with tracking details</li>
              <li>Link to track your order in real-time</li>
            </ul>
            <p className="text-[#525252] leading-relaxed mt-4">
              You can also track your order status in your Sol Digital dashboard under "Orders".
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">7. Delivery Attempts</h2>
            <p className="text-[#525252] leading-relaxed mb-4">
              Our shipping partners will make up to 3 delivery attempts:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>If you're unavailable, the courier will leave a delivery attempt notice</li>
              <li>You can reschedule delivery by contacting the courier directly</li>
              <li>After 3 failed attempts, the package will be returned to us</li>
              <li>Re-delivery charges may apply for returned packages</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">8. Delivery Address</h2>
            <p className="text-[#525252] leading-relaxed mb-4">
              Please ensure your shipping address is complete and accurate:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Full name of the recipient</li>
              <li>Complete address with house/flat number, street name</li>
              <li>Landmark for easier identification</li>
              <li>City, State, and PIN code</li>
              <li>Active mobile number for delivery coordination</li>
            </ul>
            <p className="text-[#525252] leading-relaxed mt-4">
              <strong>Note:</strong> Sol Digital is not responsible for delivery delays or failures due to incorrect or incomplete addresses.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">9. Address Changes</h2>
            <p className="text-[#525252] leading-relaxed">
              Address changes are only possible before the order is dispatched. Once shipped, the delivery address cannot be modified. Contact us immediately at support@soldigital.in if you need to change your delivery address.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">10. Package Contents</h2>
            <p className="text-[#525252] leading-relaxed mb-4">
              Your Sol Digital package will include:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>1x Premium NFC Business Card</li>
              <li>Quick Start Guide with setup instructions</li>
              <li>Protective card sleeve</li>
              <li>Invoice/Tax document</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">11. Damaged Shipments</h2>
            <p className="text-[#525252] leading-relaxed mb-4">
              If your package arrives damaged:
            </p>
            <ol className="list-decimal list-inside text-[#525252] space-y-2">
              <li>Document the damage with photos/videos before opening</li>
              <li>Report the damage to our support team within 24 hours</li>
              <li>Do not discard the packaging material</li>
              <li>We will arrange for a replacement at no additional cost</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">12. Lost Shipments</h2>
            <p className="text-[#525252] leading-relaxed">
              If your shipment is lost in transit, please contact us with your order ID. We will initiate an investigation with our shipping partner. If the package is confirmed lost, we will send a replacement or issue a full refund within 7-10 business days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">13. Non-Serviceable Areas</h2>
            <p className="text-[#525252] leading-relaxed">
              Some remote areas may not be serviceable by our shipping partners. If your PIN code is non-serviceable, we will notify you within 24 hours of placing the order and offer:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2 mt-4">
              <li>An alternative nearby address for delivery</li>
              <li>Full refund if no suitable alternative is available</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">14. Contact Us</h2>
            <p className="text-[#525252] leading-relaxed">
              For shipping-related queries, please contact us:
            </p>
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6 mt-4">
              <p className="text-[#0A0A0A] font-medium">Sol Digital Shipping Support</p>
              <p className="text-[#525252]">Email: shipping@soldigital.in</p>
              <p className="text-[#525252]">Support: support@soldigital.in</p>
              <p className="text-[#525252]">Response Time: Within 24 hours</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;
